/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { seedData } from '../data/seed'
import type { FinanceData, FinanceTransaction } from '../types'

const LOCAL_KEY = 'armin-finance-v3-data'
const LOCAL_AUTH_KEY = 'armin-finance-v3-session'
const apiMode = import.meta.env.VITE_API_MODE === 'cloudflare'
const cloneSeed = () => structuredClone(seedData)

export interface AppUser { uid: string; displayName: string; username: string }

interface FinanceContextValue {
  data: FinanceData
  user: AppUser | null
  loading: boolean
  cloudMode: boolean
  saving: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  saveData: (data: FinanceData) => Promise<void>
  refreshData: () => Promise<void>
  addTransaction: (transaction: FinanceTransaction) => Promise<void>
  deleteTransaction: (id: string) => Promise<void>
  markInstallmentPaid: (id: string, paid: boolean) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  resetData: () => Promise<void>
}

const FinanceContext = createContext<FinanceContextValue | null>(null)

function normalizeData(value: Partial<FinanceData> | null | undefined): FinanceData {
  const base = cloneSeed()
  if (!value) return base
  return {
    accounts: Array.isArray(value.accounts) ? value.accounts : base.accounts,
    categories: Array.isArray(value.categories) ? value.categories : base.categories,
    transactions: Array.isArray(value.transactions) ? value.transactions : base.transactions,
    loans: Array.isArray(value.loans) ? value.loans : base.loans,
    installments: Array.isArray(value.installments) ? value.installments : base.installments,
    debts: Array.isArray(value.debts) ? value.debts : [],
    futureExpenses: Array.isArray(value.futureExpenses) ? value.futureExpenses : [],
    assets: Array.isArray(value.assets) ? value.assets : [],
    notifications: Array.isArray(value.notifications) ? value.notifications : [],
    settings: value.settings ? { ...base.settings, ...value.settings } : base.settings,
  }
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...options,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...options.headers },
  })
  const body = await response.json().catch(() => ({})) as { error?: string }
  if (!response.ok) throw Object.assign(new Error(body.error || 'خطای ارتباط با سرور.'), { code: body.error || response.status })
  return body as T
}

export function FinanceProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<FinanceData>(() => {
    try { return normalizeData(JSON.parse(localStorage.getItem(LOCAL_KEY) || '')) } catch { return cloneSeed() }
  })
  const [user, setUser] = useState<AppUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const loadCloudData = useCallback(async () => {
    const result = await api<{ data: FinanceData }>('/state')
    const normalized = normalizeData(result.data)
    setData(normalized)
    localStorage.setItem(LOCAL_KEY, JSON.stringify(normalized))
  }, [])

  useEffect(() => {
    let active = true
    const start = async () => {
      if (!apiMode) {
        if (sessionStorage.getItem(LOCAL_AUTH_KEY)) setUser({ uid: 'local-armin', username: 'armin', displayName: 'آرمین' })
        setLoading(false)
        return
      }
      try {
        const result = await api<{ user: AppUser }>('/session')
        if (!active) return
        setUser(result.user)
        await loadCloudData()
      } catch {
        if (active) setUser(null)
      } finally {
        if (active) setLoading(false)
      }
    }
    void start()
    return () => { active = false }
  }, [loadCloudData])

  const login = useCallback(async (username: string, password: string) => {
    if (!username.trim() || !password) throw new Error('نام کاربری و رمز عبور را وارد کن.')
    if (!apiMode) {
      if (username.trim().toLowerCase() !== 'armin') throw new Error('نام کاربری اشتباه است.')
      sessionStorage.setItem(LOCAL_AUTH_KEY, '1')
      setUser({ uid: 'local-armin', username: 'armin', displayName: 'آرمین' })
      return
    }
    const result = await api<{ user: AppUser }>('/login', {
      method: 'POST', body: JSON.stringify({ username: username.trim().toLowerCase(), password }),
    })
    setUser(result.user)
    await loadCloudData()
  }, [loadCloudData])

  const logout = useCallback(async () => {
    sessionStorage.removeItem(LOCAL_AUTH_KEY)
    if (apiMode) await api('/logout', { method: 'POST', body: '{}' }).catch(() => undefined)
    setUser(null)
  }, [])

  const saveData = useCallback(async (next: FinanceData) => {
    const normalized = normalizeData(next)
    setData(normalized)
    localStorage.setItem(LOCAL_KEY, JSON.stringify(normalized))
    if (!apiMode || !user) return
    setSaving(true)
    try { await api('/state', { method: 'PUT', body: JSON.stringify({ data: normalized }) }) }
    finally { setSaving(false) }
  }, [user])

  const refreshData = useCallback(async () => { if (apiMode && user) await loadCloudData() }, [loadCloudData, user])

  const addTransaction = useCallback(async (transaction: FinanceTransaction) => {
    if (data.transactions.some((item) => item.id === transaction.id)) return
    await saveData({ ...data, transactions: [transaction, ...data.transactions] })
  }, [data, saveData])

  const deleteTransaction = useCallback(async (id: string) => {
    await saveData({ ...data, transactions: data.transactions.filter((item) => item.id !== id) })
  }, [data, saveData])

  const markInstallmentPaid = useCallback(async (id: string, paid: boolean) => {
    const today = new Intl.DateTimeFormat('fa-IR-u-ca-persian', { year: 'numeric', month: '2-digit', day: '2-digit' })
      .format(new Date()).replace(/\u200e/g, '')
    await saveData({ ...data, installments: data.installments.map((item) => item.id === id ? {
      ...item, status: paid ? 'Paid' : 'Pending', paidDateJalali: paid ? today : undefined, paidAmount: paid ? item.amount : undefined,
    } : item) })
  }, [data, saveData])

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    if (!apiMode) return
    await api('/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) })
  }, [])

  const resetData = useCallback(async () => saveData(cloneSeed()), [saveData])

  const value = useMemo(() => ({ data, user, loading, cloudMode: apiMode, saving, login, logout, saveData, refreshData, addTransaction, deleteTransaction, markInstallmentPaid, changePassword, resetData }),
    [data, user, loading, saving, login, logout, saveData, refreshData, addTransaction, deleteTransaction, markInstallmentPaid, changePassword, resetData])
  return <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>
}

export function useFinance() {
  const value = useContext(FinanceContext)
  if (!value) throw new Error('FinanceProvider missing')
  return value
}
